import { useState } from 'react';

import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useManagerTaskContext } from '@sorare/core/src/contexts/managerTask';
import { useInSeasonLocalStorage } from '@sorare/core/src/hooks/search/useInSeasonLocalStorage';
import useQueryString from '@sorare/core/src/hooks/useQueryString';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import {
  SaleType,
  TOKENS_STACKED_LIMIT,
  createCardSorts,
} from '@sorare/core/src/lib/algolia';
import { TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import ManagersSales from '@sorare/marketplace/src/components/market/OnboardingDialog/ManagersSales';
import PageTemplate from '@sorare/marketplace/src/components/market/PageTemplate';
import useDefaultFilters from '@sorare/marketplace/src/hooks/useDefaultFilters';

import { FootballCardSearch } from 'components/searchCards/FootballCardSearch';
import useFavPlayer from 'hooks/useFavPlayer';
import { SelectedLeaderboardBanner } from 'pages/TransferMarket/SelectedLeaderboardBanner';
import { secondaryMarketFilters } from 'pages/TransferMarket/cardFilters';

const ManagerSalesPage = () => {
  useTitleAndDescription(
    metadatas.transfers.title,
    metadatas.transfers.description
  );
  const { task } = useManagerTaskContext();
  const [showOnboardingDialog, setShowOnboardingDialog] = useState(true);
  const [forceShowOnboardingDialog, setForceShowOnboardingDialog] =
    useState(false);
  const unstacked = useQueryString(SEARCH_PARAMS.UNSTACKED);
  const { algoliaCardIndexes } = useConfigContext();
  const favPlayerHit = useFavPlayer({ skip: !task });

  const displayStackedView = !unstacked;
  const [inSeason] = useInSeasonLocalStorage();

  if (showOnboardingDialog && task) setShowOnboardingDialog(false);

  const filters = useDefaultFilters({
    secondary: true,
    onlySettlableCards: true,
    excludePrimaryOffers: true,
  });

  const cardSortsWithBestValue = createCardSorts({
    indexPrefix: 'Cards On Sale',
    withBestValue: true,
    withHighestPrice: false,
    withHighestAverageScore: true,
    withPopularPlayer: true,
    withEndingSoon: false,
  });

  return (
    <PageTemplate showBack>
      {showOnboardingDialog && (
        <ManagersSales
          open={forceShowOnboardingDialog}
          onClick={() => setForceShowOnboardingDialog(false)}
        />
      )}
      <FootballCardSearch
        cardFilters={secondaryMarketFilters}
        analyticsTags={['TransferMarket', 'Football']}
        sorts={cardSortsWithBestValue}
        defaultSort="Cards On Sale Popular Player"
        defaultFilters={filters}
        removeEndedSingleSaleOffers
        initialIndexUIState={{
          ...(displayStackedView && {
            toggle: {
              [TOGGLE_FILTERS.inSeasonFilter.attribute]: inSeason,
            },
          }),
          sortBy: displayStackedView
            ? algoliaCardIndexes['Cards On Sale Popular Player']
            : algoliaCardIndexes['Cards On Sale Newly Listed'],
        }}
        distinct={displayStackedView ? TOKENS_STACKED_LIMIT : false}
        stackableTypes={[SaleType.SINGLE_SALE_OFFER]}
        attributesToRetrieve={[
          'asset_id',
          'sale',
          'player',
          'rarity',
          'season',
          'slug',
        ]}
        banner={<SelectedLeaderboardBanner />}
        {...(task ? { favPlayerHit } : {})}
      />
    </PageTemplate>
  );
};

export default ManagerSalesPage;
